/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * UPLOAD FIX BATCH (approved 15/10/2026), fault f: ONE list of the file types
 * a client or officer may upload as a case document, used by every upload
 * screen (documents first, chat, checklist, return visit, delegate page,
 * Manual Upload, gap upload, Migrate separate files, Invite with documents)
 * and by the server's check. A refused file is named with its reason; the
 * others still upload. No dashes in any text here.
 *
 * Unchanged and outside this list: the Migrate zip pack (its own list and
 * signature checks), the policy, terms and signed questionnaire pickers (PDF,
 * not case documents) and video on Manual Upload.
 */

export const MAX_UPLOAD_BYTES = 30 * 1024 * 1024;
export const MAX_UPLOAD_LABEL = '30 MB';

// Accepted: PDF, JPEG, PNG, GIF, WebP, HEIC/HEIF, TIFF, DOCX, XLSX, CSV, TXT.
export const ACCEPTED_EXTENSIONS = Object.freeze(['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'tif', 'tiff', 'docx', 'xlsx', 'csv', 'txt']);
// The file picker's accept attribute (image/* lets a phone offer its photos).
export const UPLOAD_ACCEPT = ACCEPTED_EXTENSIONS.map(e => '.' + e).join(',') + ',application/pdf,image/*';

// Refused, each with its reason.
const REFUSED = Object.freeze({
  doc: 'Word 97 to 2003 files (.doc) cannot be read. Please save it as .docx or PDF and upload it again.',
  xls: 'Excel 97 to 2003 files (.xls) cannot be read reliably. Please save it as .xlsx or PDF and upload it again.',
  ppt: 'presentations cannot be read. Please save it as PDF and upload it again.',
  pptx: 'presentations cannot be read. Please save it as PDF and upload it again.',
  rtf: 'RTF files cannot be read reliably. Please save it as .docx or PDF and upload it again.',
  zip: 'archives cannot be uploaded here. Please upload the documents inside it one by one.',
  rar: 'archives cannot be uploaded here. Please upload the documents inside it one by one.',
  '7z': 'archives cannot be uploaded here. Please upload the documents inside it one by one.',
  exe: 'programs cannot be uploaded.',
  bmp: 'BMP images cannot be read. Please save it as JPEG, PNG or PDF and upload it again.',
});
const OTHER = 'this type of file cannot be read. Please upload a PDF, a photo (JPEG, PNG or HEIC), or a Word (.docx) or Excel (.xlsx) file.';

const extOf = (name) => { const n = String(name || '').toLowerCase(); const i = n.lastIndexOf('.'); return i >= 0 ? n.slice(i + 1) : ''; };

// The browser's check, from the name and size: { ok, reason }.
export function checkUploadFile(file) {
  const name = String((file && file.name) || '');
  const size = Number(file && file.size) || 0;
  const ext = extOf(name);
  if (size > MAX_UPLOAD_BYTES) return { ok: false, reason: `it is larger than ${MAX_UPLOAD_LABEL}. Please upload a smaller copy, or split it into parts.` };
  if (!size && file && 'size' in file) return { ok: false, reason: 'the file is empty.' };
  if (ACCEPTED_EXTENSIONS.includes(ext)) return { ok: true };
  // A photo from a phone can arrive with no extension but an image type.
  if (!ext && /^image\//.test(String((file && file.type) || ''))) return { ok: true };
  return { ok: false, reason: REFUSED[ext] || OTHER };
}

// One line per refused file, for the screen.
export function refusalLine(name, reason) {
  return `${String(name || 'This file')} cannot be uploaded: ${reason}`;
}

// Split a chosen list into the files that may upload and the lines for those refused.
export function splitUploads(files) {
  const ok = [], refused = [];
  for (const f of Array.from(files || [])) {
    const c = checkUploadFile(f);
    if (c.ok) ok.push(f); else refused.push(refusalLine(f && f.name, c.reason));
  }
  return { ok, refused };
}

// The server's check, from the file's content (api/_doc-prepare.js detectType):
// { ok, reason }. A program renamed .pdf is refused here, whatever its name.
export function checkUploadContent(detected, size) {
  if (Number(size) > MAX_UPLOAD_BYTES) return { ok: false, reason: `it is larger than ${MAX_UPLOAD_LABEL}.` };
  const k = detected && detected.kind;
  if (k === 'pdf' || k === 'image' || k === 'docx' || k === 'xlsx' || k === 'text') {
    if (detected.mime === 'image/bmp') return { ok: false, reason: REFUSED.bmp };
    return { ok: true };
  }
  if (k === 'ole') return { ok: false, reason: REFUSED[detected.ext] || REFUSED.doc };
  if (k === 'pptx') return { ok: false, reason: REFUSED.pptx };
  if (k === 'zip') return { ok: false, reason: REFUSED.zip };
  if (k === 'exe') return { ok: false, reason: REFUSED.exe };
  return { ok: false, reason: OTHER };
}
